import { FileText, Calendar, MessageSquare, type LucideIcon } from 'lucide-react';
import Slide from '@/components/shared/Slide';
import { Separator } from '@/components/ui/separator';
import type { DecisionMemo, DecisionMemoCard, DecisionMemoStep, ViewMode } from '@/types/roadmap.types';

interface Props {
  mode: ViewMode;
  memo?: DecisionMemo;
}

// ---------------------------------------------------------------------------
// Failsafe defaults — only render if the Worker returns `decision_memo: null`.
// The canonical default lives in apps/grant-indexer/src/decision-memo-default.ts
// and is backfilled into every client by migration 0004_decision_memo.sql.
// If you edit the copy here, update the canonical source too (and file a new
// D1 migration if you want existing clients re-seeded).
// ---------------------------------------------------------------------------

const DEFAULT_CARDS: DecisionMemoCard[] = [
  {
    title: 'Roadmap Review',
    description:
      'Review Primary and/or Go opportunities against timelines. Confirm fit, sequencing, and what “winning” looks like; widen to Conditional and Monitor when you are ready.',
    icon: 'FileText',
  },
  {
    title: 'Deadlines & Conditional Criteria',
    description:
      'With Primary and/or Go timelines in view, pressure-test dates against Conditional criteria (what would flip a call to Go), rolling gates, and internal readiness — capacity, data, partners, and approvals — so dates are decisions, not surprises.',
    icon: 'Calendar',
  },
  {
    title: 'Review Session',
    description:
      'Go/no-go working session on the open calls; widen to Conditional as needed, align on next steps, owners, and the next 30–60 days.',
    icon: 'MessageSquare',
  },
];

const DEFAULT_STEPS: DecisionMemoStep[] = [
  {
    label: 'Review',
    description: 'Review Primary and/or Go opportunities against timelines.',
  },
  {
    label: 'Decide',
    description:
      'Decide which grants to pursue now, which to sequence, and which to monitor or pass.',
  },
  {
    label: 'Finalize',
    description: 'Hold the go/no-go conversation: confirm direction and align on next steps.',
  },
];

const ICON_MAP: Record<string, LucideIcon> = {
  FileText,
  Calendar,
  MessageSquare,
};

const DEFAULT_ICON_ORDER: LucideIcon[] = [FileText, Calendar, MessageSquare];

function resolveIcon(name: string | undefined, fallback: LucideIcon): LucideIcon {
  if (name && ICON_MAP[name]) return ICON_MAP[name];
  return fallback;
}

export default function DecisionMemoSlide({ mode, memo }: Props) {
  const cards = memo?.cards?.length ? memo.cards : DEFAULT_CARDS;
  const steps = memo?.steps?.length ? memo.steps : DEFAULT_STEPS;
  const intro = memo?.intro;

  return (
    <Slide mode={mode}>
      <div className="w-full max-w-5xl mx-auto">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2 print:mb-1 print:text-[7pt]">
          Action Items
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-3 print:mb-2 print:text-[16pt]">
          What&rsquo;s Next
        </h2>

        {intro && (
          <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl mx-auto text-center mb-8 print:mb-4 print:text-[8pt]">
            {intro}
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10 print:grid-cols-3 print:gap-2 print:mb-4">
          {cards.map((card, i) => {
            const Icon = resolveIcon(card.icon, DEFAULT_ICON_ORDER[i] ?? FileText);
            return (
              <div
                key={`${card.title}-${i}`}
                className="rounded-xl border border-border/60 p-5 flex flex-col print:p-3 print:rounded-md"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4 print:w-7 print:h-7 print:mb-2">
                  <Icon className="w-5 h-5 text-primary print:w-3.5 print:h-3.5" />
                </div>
                <h3 className="text-sm font-semibold text-foreground mb-2 print:text-[8pt] print:mb-1">
                  {card.title}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed flex-1 print:text-[7pt]">
                  {card.description}
                </p>
              </div>
            );
          })}
        </div>

        <Separator className="mb-6 print:mb-3" />

        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-5 print:mb-3 print:text-[7pt]">
          Next Steps
        </p>

        <div className="relative">
          <div className="absolute top-4 left-[16.667%] right-[16.667%] h-0.5 bg-border/40 print:top-3" />
          <div className="grid grid-cols-3 gap-5 relative print:gap-2">
            {steps.map((step, i) => (
              <div key={`${step.label}-${i}`} className="flex flex-col items-center text-center">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold mb-3 ring-4 ring-background print:w-6 print:h-6 print:mb-1.5 print:ring-2 print:text-[7pt]">
                  {i + 1}
                </div>
                <span className="text-sm font-semibold text-foreground mb-1 print:text-[8pt] print:mb-0.5">
                  {step.label}
                </span>
                <p className="text-xs text-muted-foreground leading-relaxed max-w-[220px] print:text-[7pt] print:max-w-[160px]">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Slide>
  );
}
